import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getApiBaseUrl } from '../../config/apiBase';
import '../../styles/components/PostGroup.css';

const groupTypes = [
  { value: 'Congregation', label: 'Church / congregation' },
  { value: 'Ministry', label: 'Ministry / nonprofit' },
  { value: 'Business', label: 'Business / cooperative' },
  { value: 'Community', label: 'Community group' },
  { value: 'Council', label: 'Council / coordination body' },
  { value: 'Mission', label: 'Mission team' },
  { value: 'Project', label: 'Project team' },
  { value: 'Virtual', label: 'Online group' },
  { value: 'Club', label: 'Club / interest group' },
];

const groupCategories = [
  'Faith',
  'Charity',
  'Education',
  'Farming',
  'Technology',
  'Business',
  'Crafts',
  'Services',
  'Health',
  'Defense',
  'Media',
  'Housing',
  'Coordination',
  'Finance',
  'Hospitality',
  'Environmental',
];

const conversationPrompts = [
  'I can help you create a group, sign up your church, or create a ministry support page. Tell me what you want to start, even if it is still rough.',
  'Who is this for, and what kind of group is it — a church, ministry, business/co-op, service team, council, or something else?',
  'What mission should people understand, and where is it based?',
  'Who can join: everyone, people who apply, or invitation only?',
  'If this is a church, ministry, or mission, what contact info, work, needs, resources, and support details should be visible?',
  'Do you have a wide banner photo and a square thumbnail or logo for the group? You can add both when you review the draft.',
  'That is enough to prepare a first draft. You can use it now or keep clarifying.',
];

const getInitialGroup = () => {
  const signalParams = new URLSearchParams(window.location.search || '');
  const signalTitle = signalParams.get('signalTitle') || '';
  const signalType = signalParams.get('signalType') || '';
  const signalLocation = signalParams.get('location') || '';
  const signalTags = signalParams.get('tags') || '';
  const missionPrefill = [
    signalTitle && `Mission signal: ${signalTitle}`,
    signalLocation && `Place focus: ${signalLocation}`,
    signalType && `Signal type: ${signalType}`,
    signalTags && `Mission tags: ${signalTags}`,
  ].filter(Boolean).join('\n');

  return {
    GroupName: signalTitle ? `${signalTitle} Community` : '',
    GroupDescription: signalTitle ? `A member-led community organized around ${signalTitle}.` : '',
    GroupMission: missionPrefill,
    PercentToApproveCampaign: 51,
    ParentGroupId: '',
    SecededFromGroupId: '',
    GroupType: signalTitle ? 'Mission' : '',
    GroupCategory: signalTitle ? 'Faith' : '',
    AddressOne: '',
    City: '',
    State: '',
    PostalCode: '',
    Country: '',
    FounderId: '',
    EnrollmentFee: '',
    MonthlyDues: '',
    MonthlyDividends: '',
    EligibleTo: signalTitle ? 'Application' : '',
    CongregationName: '',
    CongregationStatus: 'Suggested',
    DenominationOrTradition: '',
    ClergyContact: '',
    MeetingTimes: '',
    WebsiteUrl: '',
    SocialLinks: '',
    MemberCountEstimate: '',
    MinistriesOffered: '',
    Needs: '',
    ResourcesAvailable: '',
    RepresentativeRole: '',
    RepresentationAuthorizationNotes: '',
    CreatedOn: new Date().toISOString(),
  };
};

const initialGuideMessages = [
  {
    role: 'assistant',
    content: conversationPrompts[0],
  },
];

const textFromMessages = (messages) =>
  messages
    .filter((message) => message.role === 'user')
    .map((message) => message.content)
    .join('\n')
    .toLowerCase();

const firstMatch = (text, pattern) => {
  const match = text.match(pattern);
  return match?.[1]?.trim() || '';
};

const inferGroupDraft = (currentGroup, messages) => {
  const notes = textFromMessages(messages);
  const isChurch = /church|parish|congregation|ekklesia|pastor|elder|service|sunday|denomination|fellowship/.test(notes);
  const isBusiness = /business|co-?op|cooperative|market|shop|company/.test(notes);
  const isCouncil = /council|governance|policy|board/.test(notes);
  const isMinistry = /ministry|nonprofit|charity|outreach|mission/.test(notes);
  const groupType = currentGroup.GroupType || (isChurch ? 'Congregation' : isBusiness ? 'Business' : isCouncil ? 'Council' : isMinistry ? 'Ministry' : 'Community');
  const firstSentence = messages.find((message) => message.role === 'user')?.content?.split(/[.!?]/)[0]?.trim() || '';
  const nameHint = firstMatch(notes, /(?:called|named|name is|church is|group is)\s+([^.\n]+)/i);
  const cityHint = firstMatch(notes, /\b(?:in|near|around|based in)\s+([a-z .'-]+?)(?:,|\.|\n|$)/i);
  const memberEstimate = firstMatch(notes, /(\d{1,6})\s+(?:members|people|students|families|attendees)/i);

  return {
    ...currentGroup,
    GroupType: groupType,
    GroupCategory: currentGroup.GroupCategory || (isChurch ? 'Faith' : isBusiness ? 'Business' : isCouncil ? 'Governance' : isMinistry ? 'Charity' : 'Faith'),
    GroupName: currentGroup.GroupName || nameHint || (isChurch ? 'New Church Community' : firstSentence || 'New TYK Group'),
    GroupDescription: currentGroup.GroupDescription || firstSentence || 'A group forming on ThousandYearKingdom to coordinate faithful work together.',
    GroupMission: currentGroup.GroupMission || messages.filter((message) => message.role === 'user').map((message) => message.content).join('\n\n'),
    City: currentGroup.City || cityHint,
    EligibleTo: currentGroup.EligibleTo || (isChurch ? 'Application' : 'Everyone'),
    CongregationName: currentGroup.CongregationName || (isChurch ? currentGroup.GroupName || nameHint : ''),
    CongregationStatus: currentGroup.CongregationStatus || 'Suggested',
    DenominationOrTradition: currentGroup.DenominationOrTradition || firstMatch(notes, /(?:tradition|denomination)\s+(?:is|:)?\s*([^.\n]+)/i),
    MeetingTimes: currentGroup.MeetingTimes || firstMatch(notes, /(?:meets?|services?)\s+(?:at|on)?\s*([^.\n]+)/i),
    MemberCountEstimate: currentGroup.MemberCountEstimate || memberEstimate,
    RepresentativeRole: currentGroup.RepresentativeRole || (isChurch ? 'Member / representative' : ''),
  };
};

const mergeGroupDraft = (currentGroup, draft) => {
  if (!draft || typeof draft !== 'object') {
    return currentGroup;
  }

  return Object.keys(currentGroup).reduce((next, key) => {
    if (Object.prototype.hasOwnProperty.call(draft, key) && draft[key] !== null && draft[key] !== undefined) {
      next[key] = String(draft[key]);
    }
    return next;
  }, { ...currentGroup });
};

const normalizeText = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\b(the|a|an|church|congregation|parish|ministries|ministry|inc|llc|corp|corporation)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getGroupId = (group) => group?.id ?? group?.groupId ?? group?.GroupId ?? group?.Id;
const getGroupName = (group) => group?.groupName ?? group?.GroupName ?? group?.name ?? group?.Name ?? '';
const getGroupType = (group) => group?.groupType ?? group?.GroupType ?? '';
const getGroupCity = (group) => group?.city ?? group?.City ?? '';
const getGroupState = (group) => group?.state ?? group?.State ?? '';
const getGroupCountry = (group) => group?.country ?? group?.Country ?? '';
const getGroupDescription = (group) => group?.groupDescription ?? group?.GroupDescription ?? '';
const getGroupTradition = (group) => group?.denominationOrTradition ?? group?.DenominationOrTradition ?? '';
const getGroupMembers = (group) => group?.enrolledMembers ?? group?.subscriptions ?? group?.Subscriptions ?? group?.memberCountEstimate ?? group?.MemberCountEstimate ?? '';

const scorePossibleDuplicate = (draft, group) => {
  const draftName = normalizeText(draft.GroupName || draft.CongregationName);
  const candidateName = normalizeText(getGroupName(group));
  if (!draftName || !candidateName) {
    return null;
  }

  const draftWords = draftName.split(' ').filter((word) => word.length > 2);
  const candidateWords = new Set(candidateName.split(' ').filter((word) => word.length > 2));
  const sharedWords = draftWords.filter((word) => candidateWords.has(word));
  const nameOverlap = draftWords.length ? sharedWords.length / draftWords.length : 0;
  const sameName = draftName === candidateName;
  const oneContainsOther = draftName.includes(candidateName) || candidateName.includes(draftName);
  const sameCity = draft.City && getGroupCity(group) && normalizeText(draft.City) === normalizeText(getGroupCity(group));
  const sameState = draft.State && getGroupState(group) && normalizeText(draft.State) === normalizeText(getGroupState(group));
  const sameCountry = draft.Country && getGroupCountry(group) && normalizeText(draft.Country) === normalizeText(getGroupCountry(group));
  const sameType = draft.GroupType && getGroupType(group) && normalizeText(draft.GroupType) === normalizeText(getGroupType(group));
  const sameTradition = draft.DenominationOrTradition && getGroupTradition(group)
    && normalizeText(draft.DenominationOrTradition) === normalizeText(getGroupTradition(group));

  let score = 0;
  if (sameName) score += 8;
  if (oneContainsOther) score += 5;
  score += Math.round(nameOverlap * 5);
  if (sameCity) score += 3;
  if (sameState) score += 1;
  if (sameCountry) score += 1;
  if (sameType) score += 1;
  if (sameTradition) score += 1;

  if (score < 6) {
    return null;
  }

  const reasons = [
    sameName && 'same name',
    !sameName && oneContainsOther && 'very similar name',
    !sameName && !oneContainsOther && nameOverlap >= 0.5 && 'overlapping name words',
    sameCity && 'same city',
    sameState && 'same state/region',
    sameCountry && 'same country',
    sameType && 'same type',
    sameTradition && 'same tradition',
  ].filter(Boolean);

  return { group, score, reasons };
};

const buildDuplicateCheckKey = (draft) => [
  normalizeText(draft.GroupName || draft.CongregationName),
  normalizeText(draft.GroupType),
  normalizeText(draft.City),
  normalizeText(draft.State),
  normalizeText(draft.Country),
].join('|');

const toNumberOrZero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export function PostGroup() {
  const [formData, setFormData] = useState(getInitialGroup);
  const [mode, setMode] = useState('guide');
  const [messages, setMessages] = useState(initialGuideMessages);
  const [guideInput, setGuideInput] = useState('');
  const [guideLoading, setGuideLoading] = useState(false);
  const [draftReviewLoading, setDraftReviewLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [readyToDraft, setReadyToDraft] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [createdGroup, setCreatedGroup] = useState(null);
  const [existingGroupMatch, setExistingGroupMatch] = useState(null);
  const [imageFiles, setImageFiles] = useState({ banner: null, thumbnail: null });
  const [imagePreviews, setImagePreviews] = useState({ banner: '', thumbnail: '' });
  const submitInFlightRef = useRef(false);
  const guideChatRef = useRef(null);
  const [duplicateReview, setDuplicateReview] = useState({
    status: 'idle',
    candidates: [],
    checkedKey: '',
    acknowledgedKey: '',
  });
  const { getAccessTokenSilently, user } = useAuth0();

  const isCongregation = formData.GroupType === 'Congregation';
  const isMinistry = formData.GroupType === 'Ministry';
  const supportsPublicSupportDetails = isCongregation || isMinistry;
  const liveDraft = useMemo(() => inferGroupDraft(formData, messages), [formData, messages]);

  useEffect(() => {
    const chat = guideChatRef.current;
    if (chat) chat.scrollTop = chat.scrollHeight;
  }, [messages, guideLoading]);

  useEffect(() => {
    if (user?.sub) {
      setFormData(prev => (
        prev.FounderId === user.sub ? prev : { ...prev, FounderId: user.sub }
      ));
    }
  }, [user?.sub]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const requestGuideTurn = async (nextMessages) => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      });

      const res = await fetch(`${getApiBaseUrl()}/api/socraticguide/group-interview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages: nextMessages,
          currentGroup: formData,
        }),
      });

      if (!res.ok) {
        return null;
      }

      return res.json();
    } catch {
      return null;
    }
  };

  const loadPossibleDuplicateCandidates = async (draft) => {
    const name = draft.GroupName || draft.CongregationName;
    if (!name || normalizeText(name).length < 4) {
      setDuplicateReview((current) => ({
        ...current,
        status: 'idle',
        candidates: [],
        checkedKey: buildDuplicateCheckKey(draft),
      }));
      return [];
    }

    const checkKey = buildDuplicateCheckKey(draft);
    setDuplicateReview((current) => ({ ...current, status: 'checking', checkedKey: checkKey }));

    try {
      const requests = [];
      const searchParams = new URLSearchParams();
      searchParams.set('query', name);
      if (draft.City) searchParams.set('city', draft.City);
      if (draft.State) searchParams.set('state', draft.State);

      if (draft.GroupType === 'Congregation' || /church|congregation|parish/i.test(name)) {
        requests.push(
          fetch(`${getApiBaseUrl()}/api/ChurchInvites/churches?${searchParams.toString()}`)
            .then((response) => (response.ok ? response.json() : []))
            .catch(() => []),
        );
      }

      requests.push((async () => {
        try {
          const token = await getAccessTokenSilently({
            authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
          });
          const response = await fetch(`${getApiBaseUrl()}/api/GroupsTbls`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          return response.ok ? response.json() : [];
        } catch {
          return [];
        }
      })());

      const results = await Promise.all(requests);
      const unique = new Map();
      results.flat().filter(Boolean).forEach((group) => {
        const id = getGroupId(group);
        const key = id ? `id-${id}` : `${normalizeText(getGroupName(group))}-${normalizeText(getGroupCity(group))}`;
        if (key && !unique.has(key)) {
          unique.set(key, group);
        }
      });

      const candidates = Array.from(unique.values())
        .map((group) => scorePossibleDuplicate(draft, group))
        .filter(Boolean)
        .sort((a, b) => b.score - a.score)
        .slice(0, 4);

      setDuplicateReview((current) => ({
        ...current,
        status: candidates.length ? 'found' : 'clear',
        candidates,
        checkedKey: checkKey,
      }));
      return candidates;
    } catch {
      setDuplicateReview((current) => ({
        ...current,
        status: 'error',
        candidates: [],
        checkedKey: checkKey,
      }));
      return [];
    }
  };

  const confirmDifferentGroup = () => {
    setDuplicateReview((current) => ({
      ...current,
      status: 'dismissed',
      acknowledgedKey: current.checkedKey || buildDuplicateCheckKey(formData),
    }));
    setStatusMessage('Got it. This appears to be a different group, so you can continue creating it.');
  };

  const sendGuideMessage = async () => {
    const answer = guideInput.trim();
    if (!answer) {
      return;
    }

    const userMessages = [...messages, { role: 'user', content: answer }];
    const answerCount = userMessages.filter((message) => message.role === 'user').length;
    setGuideInput('');
    setMessages(userMessages);
    setGuideLoading(true);

    const guideTurn = await requestGuideTurn(userMessages);
    const fallbackDraft = inferGroupDraft(formData, userMessages);
    const assistantMessage = guideTurn?.assistantMessage || conversationPrompts[Math.min(answerCount, conversationPrompts.length - 1)];
    const guideDraft = guideTurn?.draftGroup || guideTurn?.draft || null;
    const isReady = Boolean(guideTurn?.isReadyToDraft || guideTurn?.readyToDraft) || answerCount >= 6;
    const nextDraft = mergeGroupDraft(inferGroupDraft(formData, userMessages), guideDraft || fallbackDraft);

    setFormData(nextDraft);
    setMessages([...userMessages, { role: 'assistant', content: assistantMessage }]);
    setReadyToDraft(isReady);
    setMissingFields(Array.isArray(guideTurn?.missingFields) ? guideTurn.missingFields : []);
    setGuideLoading(false);
    await loadPossibleDuplicateCandidates(nextDraft);
  };

  const useGuideDraft = async () => {
    if (draftReviewLoading) {
      return;
    }

    setDraftReviewLoading(true);
    setStatusMessage('Checking for possible duplicate churches or groups before opening the draft...');
    setErrorMessage('');

    try {
      const nextDraft = inferGroupDraft(formData, messages);
      setFormData(nextDraft);
      await loadPossibleDuplicateCandidates(nextDraft);
      setMode('details');
      setStatusMessage('I filled the group details from the conversation. Review anything important before creating it.');
      setErrorMessage('');
    } finally {
      setDraftReviewLoading(false);
    }
  };

  const clearSelectedImages = () => {
    Object.values(imagePreviews).filter(Boolean).forEach((url) => URL.revokeObjectURL?.(url));
    setImageFiles({ banner: null, thumbnail: null });
    setImagePreviews({ banner: '', thumbnail: '' });
  };

  const handleImageChange = (kind) => (event) => {
    const file = event.target.files?.[0] || null;
    setImageFiles((current) => ({ ...current, [kind]: file }));
    setImagePreviews((current) => {
      if (current[kind]) URL.revokeObjectURL?.(current[kind]);
      return {
        ...current,
        [kind]: file && URL.createObjectURL ? URL.createObjectURL(file) : '',
      };
    });
  };

  const uploadGroupImage = async (groupId, kind, file, token) => {
    if (!file) return null;
    const body = new FormData();
    body.append('image', file);
    body.append('groupId', groupId);
    body.append('column', kind);
    const response = await fetch(`${getApiBaseUrl()}/api/GroupsTbls/UploadGroupImage`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body,
    });
    if (!response.ok) {
      const problem = await response.json().catch(() => null);
      throw new Error(problem?.detail || problem?.title || `${kind} image could not be saved`);
    }
    return response.json();
  };

  const validate = () => {
    if (!formData.GroupName.trim()) {
      setErrorMessage('Give the group a name.');
      return false;
    }
    if (!formData.GroupType) {
      setErrorMessage('Choose what kind of group this is.');
      return false;
    }
    if (!formData.GroupMission.trim() && !formData.GroupDescription.trim()) {
      setErrorMessage('Add a short mission or description.');
      return false;
    }
    if (isCongregation && !formData.City.trim() && !formData.Country.trim()) {
      setErrorMessage('For a church/congregation listing, add at least a city or country so people know where it is.');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const buildPayload = () => ({
    groupName: formData.GroupName.trim(),
    groupDescription: formData.GroupDescription.trim(),
    groupMission: formData.GroupMission.trim(),
    percentToApproveCampaign: Number(formData.PercentToApproveCampaign || 51),
    parentGroupId: formData.ParentGroupId || null,
    secededFromGroupId: formData.SecededFromGroupId || null,
    groupType: formData.GroupType,
    groupCategory: formData.GroupCategory || 'Faith',
    addressOne: formData.AddressOne,
    city: formData.City,
    state: formData.State,
    postalCode: formData.PostalCode,
    country: formData.Country,
    founderId: formData.FounderId,
    enrollmentFee: toNumberOrZero(formData.EnrollmentFee),
    monthlyDues: toNumberOrZero(formData.MonthlyDues),
    monthlyDividends: toNumberOrZero(formData.MonthlyDividends),
    eligibleTo: formData.EligibleTo || (isCongregation ? 'Application' : 'Everyone'),
    congregationName: isCongregation ? (formData.CongregationName.trim() || formData.GroupName.trim()) : null,
    congregationStatus: isCongregation ? formData.CongregationStatus : null,
    denominationOrTradition: isCongregation ? formData.DenominationOrTradition.trim() || null : null,
    clergyContact: supportsPublicSupportDetails ? formData.ClergyContact.trim() || null : null,
    meetingTimes: isCongregation ? formData.MeetingTimes.trim() || null : null,
    websiteUrl: supportsPublicSupportDetails ? formData.WebsiteUrl.trim() || null : null,
    socialLinks: supportsPublicSupportDetails ? formData.SocialLinks.trim() || null : null,
    memberCountEstimate: isCongregation && formData.MemberCountEstimate ? Number(formData.MemberCountEstimate) : null,
    ministriesOffered: supportsPublicSupportDetails ? formData.MinistriesOffered.trim() || null : null,
    needs: supportsPublicSupportDetails ? formData.Needs.trim() || null : null,
    resourcesAvailable: supportsPublicSupportDetails ? formData.ResourcesAvailable.trim() || null : null,
    representativeRole: isCongregation ? formData.RepresentativeRole.trim() || null : null,
    representationAuthorizationNotes: isCongregation ? formData.RepresentationAuthorizationNotes.trim() || null : null,
    createdOn: formData.CreatedOn,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLoading || submitInFlightRef.current || createdGroup) {
      return;
    }

    if (!validate()) {
      return;
    }

    submitInFlightRef.current = true;
    setSubmitLoading(true);
    setStatusMessage('Checking for possible duplicates before creating this group...');
    setErrorMessage('');
    setExistingGroupMatch(null);

    const currentKey = buildDuplicateCheckKey(formData);
    if (duplicateReview.checkedKey !== currentKey || duplicateReview.status === 'idle') {
      const candidates = await loadPossibleDuplicateCandidates(formData);
      if (candidates.length > 0) {
        setErrorMessage('This may already exist in TYK. Please review the possible match before creating a new group.');
        setStatusMessage('');
        setSubmitLoading(false);
        submitInFlightRef.current = false;
        return;
      }
    }

    if (duplicateReview.candidates.length > 0 && duplicateReview.acknowledgedKey !== currentKey) {
      setErrorMessage('This may already exist in TYK. Please choose “This is a different group” before creating a new one.');
      setStatusMessage('');
      setSubmitLoading(false);
      submitInFlightRef.current = false;
      return;
    }

    setStatusMessage('Creating group...');
    setCreatedGroup(null);

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      });

      const res = await fetch(`${getApiBaseUrl()}/api/GroupsTbls`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(buildPayload()),
      });

      const data = await res.json().catch(() => null);
      if (res.status === 409) {
        setExistingGroupMatch({
          id: data?.existingGroupId,
          name: data?.existingGroupName || formData.GroupName,
        });
        throw new Error(data?.detail || 'This group already exists. Open the existing group instead of creating another copy.');
      }
      if (!res.ok) {
        throw new Error(data?.detail || data?.message || 'Could not create this group yet.');
      }

      const successfulGroup = {
        ...data,
        id: data?.id ?? data?.groupId,
        groupName: data?.groupName || formData.GroupName,
      };
      const imageWarnings = [];
      if (successfulGroup.id && (imageFiles.banner || imageFiles.thumbnail)) {
        setStatusMessage('Group created. Uploading its banner and thumbnail...');
        for (const kind of ['banner', 'thumbnail']) {
          try {
            await uploadGroupImage(successfulGroup.id, kind, imageFiles[kind], token);
          } catch (imageError) {
            imageWarnings.push(imageError.message);
          }
        }
      }
      setCreatedGroup({ ...successfulGroup, imageWarnings });
      setFormData({ ...getInitialGroup(), FounderId: user?.sub || '' });
      clearSelectedImages();
      setMessages(initialGuideMessages);
      setGuideInput('');
      setReadyToDraft(false);
      setMissingFields([]);
      setDuplicateReview({ status: 'idle', candidates: [], checkedKey: '', acknowledgedKey: '' });
      setStatusMessage('');
      setErrorMessage('');
    } catch (err) {
      setErrorMessage(err.message || 'Error creating group.');
      setStatusMessage('');
      submitInFlightRef.current = false;
    } finally {
      setSubmitLoading(false);
    }
  };

  const beginAnotherGroup = () => {
    submitInFlightRef.current = false;
    setCreatedGroup(null);
    setExistingGroupMatch(null);
    setStatusMessage('');
    setErrorMessage('');
    clearSelectedImages();
    setMode('guide');
  };

  const renderInlineSpinner = (label) => (
    <span className="group-button-progress" aria-live="polite">
      <span className="group-spinner" aria-hidden="true" />
      {label}
    </span>
  );

  const renderDuplicateReview = () => {
    if (duplicateReview.status === 'checking') {
      return (
        <section className="group-duplicate-review checking" aria-live="polite">
          <strong>Checking for an existing group...</strong>
          <p>Before creating a duplicate, TYK is looking for churches or groups that may already match this profile.</p>
        </section>
      );
    }

    if (!duplicateReview.candidates.length) {
      return null;
    }

    return (
      <section className="group-duplicate-review" aria-live="polite">
        <div>
          <p className="group-guide-eyebrow">Possible existing match</p>
          <h3>This may already be in TYK</h3>
          <p>
            If this is the same church or organization, open the existing group and join/request access instead of creating a duplicate.
          </p>
        </div>
        <div className="group-duplicate-list">
          {duplicateReview.candidates.map(({ group, reasons, score }) => {
            const id = getGroupId(group);
            return (
              <article key={id || `${getGroupName(group)}-${score}`} className="group-duplicate-card">
                <h4>{getGroupName(group) || 'Existing group'}</h4>
                <p>{[getGroupCity(group), getGroupState(group), getGroupCountry(group)].filter(Boolean).join(', ') || getGroupDescription(group) || 'Location not listed'}</p>
                <dl>
                  <div><dt>Type</dt><dd>{getGroupType(group) || 'Group'}</dd></div>
                  <div><dt>Members</dt><dd>{getGroupMembers(group) || 'Not listed'}</dd></div>
                  <div><dt>Why shown</dt><dd>{reasons.join(', ')}</dd></div>
                </dl>
                {id && (
                  <a className="group-primary-link" href={`/group/${id}`}>
                    This is the same — open/join it
                  </a>
                )}
              </article>
            );
          })}
        </div>
        <button type="button" className="group-secondary-button" onClick={confirmDifferentGroup}>
          This is a different group
        </button>
      </section>
    );
  };

  const renderGuide = () => (
    <section className="group-guide-panel">
      <div className="group-guide-copy">
        <p className="group-guide-eyebrow">Create with the Guide</p>
        <h2>Start a group or sign up your church</h2>
        <p>
          Talk naturally. The Guide will help clarify what you are creating, then fill a draft form for you to edit before anything is submitted.
        </p>
      </div>

      <div className="group-guide-chat" ref={guideChatRef}>
        {messages.map((message, index) => (
          <div key={`${message.role}-${index}`} className={`group-guide-message ${message.role}`}>
            <strong>{message.role === 'assistant' ? 'Guide' : 'You'}</strong>
            <p>{message.content}</p>
          </div>
        ))}
        {guideLoading && (
          <div className="group-guide-message assistant">
            <strong>Guide</strong>
            <p>Listening...</p>
          </div>
        )}
      </div>

      {messages.some((message) => message.role === 'user') && (
        <aside className="group-live-draft">
          <p className="group-guide-eyebrow">Living draft</p>
          <h3>{liveDraft.GroupName || 'Untitled group'}</h3>
          <p>{liveDraft.GroupMission || liveDraft.GroupDescription || 'The mission will sharpen as you talk.'}</p>
          <dl>
            <div><dt>Type</dt><dd>{groupTypes.find((type) => type.value === liveDraft.GroupType)?.label || 'Not sure yet'}</dd></div>
            <div><dt>Category</dt><dd>{liveDraft.GroupCategory || 'Not sure yet'}</dd></div>
            <div><dt>Who can join?</dt><dd>{liveDraft.EligibleTo || 'Not sure yet'}</dd></div>
            <div><dt>Location</dt><dd>{[liveDraft.City, liveDraft.State, liveDraft.Country].filter(Boolean).join(', ') || 'Not listed yet'}</dd></div>
          </dl>
        </aside>
      )}

      {renderDuplicateReview()}

      {readyToDraft && (
        <div className="group-guide-ready">
          <strong>Draft ready</strong>
          <p>The group profile is clear enough to review. You can keep talking, or switch to the editable details.</p>
          {missingFields.length > 0 && <p>Still worth checking: {missingFields.slice(0, 4).join(', ')}</p>}
        </div>
      )}

      <label className="group-guide-input">
        <span>Message the Guide</span>
        <textarea
          value={guideInput}
          onChange={(event) => setGuideInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              sendGuideMessage();
            }
          }}
          placeholder="Example: I want to sign up our church. We are a small non-denominational congregation in Buffalo with about 120 people..."
        />
      </label>

      <div className="group-guide-actions">
        {messages.length > 1 && (
          <button type="button" className="group-secondary-button" onClick={() => {
            setMessages(initialGuideMessages);
            setReadyToDraft(false);
            setMissingFields([]);
          }} disabled={draftReviewLoading || guideLoading}>
            Start over
          </button>
        )}
        {messages.some((message) => message.role === 'user') && (
          <button type="button" className="group-secondary-button" onClick={useGuideDraft} disabled={draftReviewLoading}>
            {draftReviewLoading
              ? renderInlineSpinner('Preparing draft...')
              : readyToDraft ? 'Review draft' : 'Use draft so far'}
          </button>
        )}
        <button type="button" className="group-primary-button" disabled={!guideInput.trim() || guideLoading} onClick={sendGuideMessage}>
          {guideLoading ? 'Listening...' : 'Send'}
        </button>
      </div>
    </section>
  );

  const renderField = (name, label, props = {}) => (
    <label>
      <span>{label}</span>
      <input
        type={props.type || 'text'}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        {...props}
      />
    </label>
  );

  const renderDetails = () => (
    <form onSubmit={handleSubmit} className="group-details-form">
      <section className="group-form-card">
        <h3>Basic profile</h3>
        {renderDuplicateReview()}
        <div className="group-form-grid">
          {renderField('GroupName', 'Name', { required: true, placeholder: 'Example: Open Table Church' })}
          <label>
            <span>Group type</span>
            <select name="GroupType" value={formData.GroupType} onChange={handleChange} required>
              <option value="">Select type</option>
              {groupTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
            </select>
          </label>
          <label>
            <span>Category</span>
            <select name="GroupCategory" value={formData.GroupCategory} onChange={handleChange}>
              <option value="">Select category</option>
              {groupCategories.map((category) => <option key={category} value={category}>{category}</option>)}
            </select>
          </label>
          <label>
            <span>Who can join?</span>
            <select name="EligibleTo" value={formData.EligibleTo} onChange={handleChange}>
              <option value="">Choose membership path</option>
              <option value="Everyone">Anyone can join</option>
              <option value="Application">People request/apply first</option>
              <option value="Invitation">Invitation only</option>
            </select>
          </label>
        </div>
        <label>
          <span>Mission</span>
          <textarea name="GroupMission" value={formData.GroupMission} onChange={handleChange} rows={4} />
        </label>
        <label>
          <span>Description</span>
          <textarea name="GroupDescription" value={formData.GroupDescription} onChange={handleChange} rows={3} />
        </label>
      </section>

      {isCongregation && (
        <section className="group-form-card">
          <h3>Church / congregation profile</h3>
          <p>These details help people recognize the church and understand how official participation should work.</p>
          <div className="group-form-grid">
            {renderField('CongregationName', 'Church/congregation name')}
            <label>
              <span>Listing status</span>
              <select name="CongregationStatus" value={formData.CongregationStatus} onChange={handleChange}>
                <option value="Suggested">Suggested by member</option>
                <option value="CommunityAttested">Community-attested</option>
                <option value="Claimed">Claimed by representative</option>
                <option value="TYKParticipating">TYK-participating</option>
              </select>
            </label>
            {renderField('DenominationOrTradition', 'Denomination / tradition')}
            {renderField('ClergyContact', 'Pastor / elder / representative contacts')}
            {renderField('MeetingTimes', 'Meeting times')}
            {renderField('MemberCountEstimate', 'Member count estimate', { type: 'number', min: '0' })}
            {renderField('WebsiteUrl', 'Website', { type: 'url', placeholder: 'https://' })}
            {renderField('SocialLinks', 'Social links')}
            {renderField('RepresentativeRole', 'Your representative role')}
            {renderField('RepresentationAuthorizationNotes', 'Authorization notes')}
          </div>
          <div className="group-form-grid group-form-grid-three">
            <label><span>Ministries offered</span><textarea name="MinistriesOffered" value={formData.MinistriesOffered} onChange={handleChange} rows={3} /></label>
            <label><span>Needs</span><textarea name="Needs" value={formData.Needs} onChange={handleChange} rows={3} /></label>
            <label><span>Resources available</span><textarea name="ResourcesAvailable" value={formData.ResourcesAvailable} onChange={handleChange} rows={3} /></label>
          </div>
        </section>
      )}

      {isMinistry && (
        <section className="group-form-card">
          <h3>Ministry support profile</h3>
          <p>These details help people understand the mission, scan a QR code at an event, answer needs, and support the work with transparency.</p>
          <div className="group-form-grid">
            {renderField('ClergyContact', 'Leader / ministry contact')}
            {renderField('WebsiteUrl', 'Website', { type: 'url', placeholder: 'https://' })}
            {renderField('SocialLinks', 'Social links')}
          </div>
          <div className="group-form-grid group-form-grid-three">
            <label><span>Ministry work</span><textarea name="MinistriesOffered" value={formData.MinistriesOffered} onChange={handleChange} rows={3} placeholder="Gospel events, prayer, discipleship, outreach..." /></label>
            <label><span>Needs</span><textarea name="Needs" value={formData.Needs} onChange={handleChange} rows={3} placeholder="Travel support, volunteers, follow-up calls, media, hospitality..." /></label>
            <label><span>Resources available</span><textarea name="ResourcesAvailable" value={formData.ResourcesAvailable} onChange={handleChange} rows={3} placeholder="Prayer team, teachers, event network, local partners..." /></label>
          </div>
        </section>
      )}

      <section className="group-form-card">
        <h3>Location and settings</h3>
        <div className="group-form-grid">
          {renderField('AddressOne', 'Street address')}
          {renderField('City', 'City')}
          {renderField('State', 'State / region')}
          {renderField('PostalCode', 'Postal code')}
          {renderField('Country', 'Country')}
          {renderField('PercentToApproveCampaign', 'Default approval percent', { type: 'number', min: '1', max: '100' })}
          {renderField('EnrollmentFee', 'Enrollment fee', { type: 'number', min: '0', step: '0.01' })}
          {renderField('MonthlyDues', 'Monthly dues', { type: 'number', min: '0', step: '0.01' })}
          {renderField('MonthlyDividends', 'Monthly dividends', { type: 'number', min: '0', step: '0.01' })}
          {renderField('ParentGroupId', 'Denomination/fellowship network ID')}
          {renderField('SecededFromGroupId', 'Separated from group ID')}
        </div>
      </section>

      <section className="group-form-card group-image-card">
        <div>
          <h3>Group images</h3>
          <p>A banner gives the public page its atmosphere. A square thumbnail or logo helps people recognize the group in Explore Communities.</p>
        </div>
        <div className="group-image-grid">
          <label className="group-image-picker">
            <span>Wide banner image</span>
            <input name="groupBanner" type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageChange('banner')} />
            <small>Best with a wide photo or graphic. You can adjust its framing later.</small>
            {imagePreviews.banner && <img src={imagePreviews.banner} alt="Selected banner preview" className="group-banner-preview" />}
          </label>
          <label className="group-image-picker">
            <span>Square thumbnail or logo</span>
            <input name="groupThumbnail" type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageChange('thumbnail')} />
            <small>Shown on the public group page and community cards. A square image works best.</small>
            {imagePreviews.thumbnail && <img src={imagePreviews.thumbnail} alt="Selected thumbnail preview" className="group-thumbnail-preview" />}
          </label>
        </div>
      </section>

      <div className="group-submit-row">
        <button type="button" className="group-secondary-button" onClick={() => setMode('guide')} disabled={submitLoading}>Back to conversation</button>
        <button type="submit" className="group-primary-button" disabled={submitLoading}>
          {submitLoading ? renderInlineSpinner('Creating group...') : 'Create group'}
        </button>
      </div>
    </form>
  );

  return (
    <main className="group-creator-shell">
      <header className="group-creator-header">
        <p className="group-guide-eyebrow">Create group</p>
        <h1>Start a community, ministry, business, or church profile</h1>
        <p>
          The easiest path is a guided conversation. When the draft feels right, review the details and create the group.
        </p>
        {!createdGroup && (
          <div className="group-mode-switch" role="tablist" aria-label="Group creation mode">
            <button type="button" className={mode === 'guide' ? 'active' : ''} onClick={() => setMode('guide')}>
              Create with the Guide
            </button>
            <button type="button" className={mode === 'details' ? 'active' : ''} onClick={() => setMode('details')}>
              Edit details
            </button>
          </div>
        )}
      </header>

      {createdGroup && (
        <section className="group-creation-success" role="status" aria-live="polite">
          <div className="group-success-check" aria-hidden="true">✓</div>
          <p className="group-guide-eyebrow">Group created</p>
          <h2>{createdGroup.groupName}</h2>
          <p>Your form has been cleared. You can now open the group, invite people, post ideas, and begin coordinating.</p>
          {createdGroup.imageWarnings?.length > 0 && (
            <p className="group-image-warning">The group was created, but one or more images did not upload. Open the group to add them again.</p>
          )}
          <div className="group-success-actions">
            {createdGroup.id && <a className="group-primary-link" href={`/group-members/${createdGroup.id}`}>Open your new group</a>}
            <button type="button" className="group-secondary-button" onClick={beginAnotherGroup}>Create another group</button>
            <a className="group-secondary-link" href="/fetch-groups">Explore communities</a>
          </div>
        </section>
      )}
      {!createdGroup && statusMessage && <p className="group-status-message">{statusMessage}</p>}
      {!createdGroup && errorMessage && <p className="group-error-message">{errorMessage}</p>}
      {!createdGroup && existingGroupMatch?.id && (
        <p className="group-existing-match-link">
          <a href={`/group/${existingGroupMatch.id}`}>Open {existingGroupMatch.name || 'the existing group'}</a>
        </p>
      )}

      {!createdGroup && (mode === 'guide' ? renderGuide() : renderDetails())}
    </main>
  );
}
